import { Fragment, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { OUTPOST_ONBOARDING_SOURCE_NAME } from '../../../../../../../domain/console-source-names';
import { OutpostDestination, OutpostEvent, OutpostTenant } from '../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import { copyTextToClipboard } from '../../../../utils/copy';
import { showChat } from '../../../../utils/liveChat';
import { storage_keys } from '../../../../utils/storage';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import Container from '../../../common/base/Container';
import Icon, { IconName } from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import CodeSnippet from '../../../common/CodeSnippet';
import { useDialog } from '../../../common/Dialog';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, ViewTitle } from '../../../layouts/ViewLayout';
import { StyledProgress } from '../../DashboardScene/Connections/Create/CreateView';
import { useOutpostConfig } from '../OutpostContext';
import { OUTPOST_BASE_URL, OUTPOST_EXAMPLE_TABS, OutpostExampleLanguage, useOutpostSnippetMaskableValue } from '../utils/outpostSnippets';
import CreateDestination from './Steps/CreateDestination';
import CreateTenant from './Steps/CreateTenant';
import DownloadSDK from './Steps/DownloadSDK';
import PublishEvent from './Steps/PublishEvent';
import SetTopics from './Steps/SetTopics';
import WhatsNext from './Steps/WhatsNext';
export const BASE_URL = OUTPOST_BASE_URL;
export interface OutpostGetStartedTestUrl {
  // ingest_url (`/e/<id>`) is the destination/send target; inspect_url (`/<id>`)
  // is the click-through that opens the Console. Not interchangeable.
  ingest_url: string;
  inspect_url: string;
  source_id: string;
}
export interface OutpostGetStartedContext {
  active_language: OutpostExampleLanguage;
  tenant: OutpostTenant | null;
  topics?: string[];
  destination: OutpostDestination | null;
  event: OutpostEvent | null;
  test_url: OutpostGetStartedTestUrl | null;
}
export interface OutpostGetStartedStepProps {
  context: OutpostGetStartedContext;
  mutateContext: (input: Partial<OutpostGetStartedContext>) => void;
  nextStep: () => void;
  is_current_step: boolean;
}
export type OutpostGetStartedStepDescription = string | ((context: OutpostGetStartedContext) => string | ReactNode);
export interface OutpostGetStartedStep {
  key: string;
  label: string;
  description: OutpostGetStartedStepDescription;
  optional?: boolean;
  icon: 'inbox' | 'download' | 'group' | 'destination' | 'send' | 'checklist';
  Component: React.ComponentType<OutpostGetStartedStepProps>;
  isDone: (context: OutpostGetStartedContext) => boolean;
}
const getPublishEventStepDescription = (context: OutpostGetStartedContext): string | ReactNode => {
  const configured_topics = context.topics;
  if (configured_topics?.length) {
    const example_topic = configured_topics[0];
    return <Fragment>
        {'Publish an event scoped to that tenant. Outpost delivers it to every destination on that tenant whose subscribed topics include '}
        <Text as="span" mono muted>
          {example_topic}
        </Text>
        .
      </Fragment>;
  }
  return 'Publish an event scoped to that tenant. Outpost delivers it to every destination on that tenant.';
};
const PROMPT_MD_URL = 'https://raw.githubusercontent.com/hookdeck/outpost/refs/heads/main/docs/agent-evaluation/hookdeck-outpost-agent-prompt.md';
export const DEFAULT_TEST_DESTINATION_URL = 'https://mock.hookdeck.com/webhook';
export const example_tabs = OUTPOST_EXAMPLE_TABS;
const interpolatePromptTemplate = (template: string, values: Record<string, string>) => template.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_, key: string) => values[key] ?? `{{${key}}}`);
const getTopicsListPromptValue = (topics?: string[]) => topics?.length ? topics.map(topic => `- \`${topic}\``).join('\n') : '- No topics configured yet.';
const extractPromptTemplateCodeBlock = (markdown: string) => {
  const template_heading_index = markdown.indexOf('## Template');
  if (template_heading_index === -1) {
    throw new Error('Template heading not found');
  }
  const template_section = markdown.slice(template_heading_index);
  const code_block_match = template_section.match(/```(?:[^\n]*)\n([\s\S]*?)```/);
  if (!code_block_match?.[1]) {
    throw new Error('Template code block not found');
  }
  return code_block_match[1];
};
export const steps: OutpostGetStartedStep[] = [{
  key: 'set_topics',
  label: 'Set your topics',
  description: "Topics are the names of the events this Outpost project recognizes, like the events you publish in your product. You can skip this if you don't need topic-based routing.",
  optional: true,
  icon: 'inbox' as const,
  Component: SetTopics,
  isDone: (context: OutpostGetStartedContext) => !!context.topics?.length || !!context.tenant
}, {
  key: 'download_sdk',
  label: 'Use the API or install an SDK',
  description: 'Implementing Outpost means using the API, either directly or through an SDK to create the necessary resources. Use the REST API or install one of the SDKs for your language.',
  optional: false,
  icon: 'download' as const,
  Component: DownloadSDK,
  isDone: (context: OutpostGetStartedContext) => !!context.tenant
}, {
  key: 'create_tenant',
  label: 'Create your first tenant',
  description: 'A tenant is an entity in your product that you publish events to, typically an org or team (or one user in a smaller app). Each tenant has destinations that determine where those events are delivered.',
  icon: 'group' as const,
  Component: CreateTenant,
  isDone: (context: OutpostGetStartedContext) => !!context.tenant
}, {
  key: 'create_destination',
  label: 'Create your first destination',
  description: 'Destinations are the webhook endpoints or services where published events are delivered. Each destination belongs to a tenant.',
  icon: 'destination' as const,
  Component: CreateDestination,
  isDone: (context: OutpostGetStartedContext) => !!context.destination
}, {
  key: 'publish_event',
  label: 'Publish an event',
  description: getPublishEventStepDescription,
  icon: 'send' as const,
  Component: PublishEvent,
  isDone: (context: OutpostGetStartedContext) => !!context.event
}, {
  key: 'whats_next',
  label: "What's next",
  description: 'Explore the next things to configure after your first event is flowing.',
  icon: 'checklist' as const,
  Component: WhatsNext,
  isDone: () => false
}];
const GetStarted: React.FC = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    config
  } = useOutpostConfig();
  const {
    addToast
  } = useToasts();
  const show_dialog = useDialog();
  const maskable_value = useOutpostSnippetMaskableValue();
  const theme = useTheme();
  const [active_language, setActiveLanguage] = useState<OutpostExampleLanguage>('http');
  const [manual_progress, setManualProgress] = useState(0);
  const {
    data: tenant
  } = useSWR(`${APIMethodKeys.outpost.tenants.list({
    limit: 1
  })}:single`, () => HookdeckAPI.outpost.tenants.list({
    limit: 1
  }).then(data => data.models.length > 0 ? data.models[0] : null), {
    refreshInterval: latest => !latest ? 3000 : 0,
    revalidateOnFocus: true
  });
  const {
    data: destination
  } = useSWR(tenant ? `${APIMethodKeys.outpost.destinations.list(tenant.id, {
    limit: 1
  })}:single` : null, () => HookdeckAPI.outpost.destinations.list(tenant?.id as string, {
    limit: 1
  }).then(data_0 => data_0.length > 0 ? data_0[0] : null), {
    refreshInterval: tenant ? latest_0 => !latest_0 ? 3000 : 0 : undefined,
    revalidateOnFocus: true
  });
  const {
    data: event
  } = useSWR(destination && tenant ? `${APIMethodKeys.outpost.events.list({
    limit: 1,
    tenant_id: tenant?.id
  })}:single` : null, () => HookdeckAPI.outpost.events.list({
    limit: 1,
    tenant_id: tenant?.id
  }).then(data_1 => data_1.models.length > 0 ? data_1.models[0] : null), {
    refreshInterval: destination ? latest_1 => !latest_1 ? 3000 : 0 : undefined,
    revalidateOnFocus: true
  });

  // Persisted to localStorage so a page refresh reuses the same source instead of
  // provisioning a new one on every load.
  const [stored_test_url, setStoredTestUrl] = useLocalStorage<OutpostGetStartedTestUrl | null>(storage_keys.outpost_get_started_test_url, null);

  // Auto-generate a test URL for the onboarding so subsequent steps can pre-fill
  // the destination URL and offer a direct link to the console for inspecting
  // received events. Only runs when nothing is cached yet; the result is stored so
  // subsequent loads reuse it.
  const {
    data: created_test_url
  } = useSWR<OutpostGetStartedTestUrl>(stored_test_url ? null : storage_keys.outpost_get_started_test_url, async () => {
    // Declared in `domain/` so a server test asserts POST /sources/new still accepts
    // it — the endpoint validates `name` against ALIAS_PATTERN and 422s anything else.
    const result = await HookdeckAPI.console.createTestUrl({
      name: OUTPOST_ONBOARDING_SOURCE_NAME
    });
    const created: OutpostGetStartedTestUrl = {
      ingest_url: result.ingest_url,
      inspect_url: result.inspect_url,
      source_id: result.source_id
    };
    setStoredTestUrl(created);
    return created;
  }, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 60 * 60 * 1000
  });
  const test_url = stored_test_url ?? created_test_url ?? null;
  const topics = config?.TOPICS?.split(',').map(topic => topic.trim()).filter(topic_0 => !!topic_0);
  const handleCopyPromptClick = async () => {
    try {
      const response = await fetch(PROMPT_MD_URL);
      if (!response.ok) {
        throw new Error(`Failed to fetch prompt template: ${response.status}`);
      }
      const prompt_markdown = await response.text();
      const prompt_template = extractPromptTemplateCodeBlock(prompt_markdown);
      const prompt = interpolatePromptTemplate(prompt_template, {
        DOCS_URL: LINKS.outpost.docs_root,
        API_BASE_URL: BASE_URL,
        TEST_DESTINATION_URL: DEFAULT_TEST_DESTINATION_URL,
        LLMS_FULL_URL: `${LINKS.outpost.docs_root}/llms.txt`,
        TOPICS_LIST: getTopicsListPromptValue(topics)
      });
      const is_copied = await copyTextToClipboard(prompt);
      if (!is_copied) {
        throw new Error('Unable to copy prompt to clipboard');
      }
      void HookdeckAPI.track.event('[Outpost] Copied Agent Prompt').catch(() => null);
      addToast('success', 'Copied to clipboard');
      show_dialog(() => null, () => null, {
        title: 'Hookdeck Outpost API Key',
        submit_label: 'Done',
        cancel_label: 'Close',
        message: <Div>
              <Text m={{
            b: 3
          }}>
                Your agent will need your <code>OUTPOST_API_KEY</code> to authenticate. Configure it
                as an environment variable in your application.
              </Text>
              <CodeSnippet code={`OUTPOST_API_KEY=YOUR_API_KEY`} language="txt" maskable_value={maskable_value} onCopySuccess={() => {
            void HookdeckAPI.track.event('[Outpost] Copied Get Started API Key Snippet', {
              source: 'agent_prompt_dialog'
            }).catch(() => null);
          }} />
            </Div>
      });
    } catch (error) {
      addToast('error', 'Could not copy prompt');
    }
  };
  const mutateContext = (input: Pick<OutpostGetStartedContext, 'active_language'>) => {
    if (input.active_language !== undefined) {
      setActiveLanguage(input.active_language);
    }
  };
  const context: OutpostGetStartedContext = {
    active_language,
    tenant: tenant ?? null,
    topics,
    destination: destination ?? null,
    event: event ?? null,
    test_url: test_url ?? null
  };
  const progress = Math.max(steps.findIndex(step => !step.isDone(context)), manual_progress);
  useEffect(() => {
    if (progress > 0) {
      HookdeckAPI.track.event('[Outpost] Completed Onboarding Step', {
        step: steps[progress].key
      });
    }
  }, [progress]);
  useEffect(() => {
    if (progress > 0) {
      const element = document.getElementById(steps[progress]?.key);
      if (!element) return;
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [progress]);
  const visible_step_index = Math.min(progress + 1, steps.length - 1);
  return <StyledViewLayout>
      <StyledViewContent>
        <StyledViewNav>
          <StyledViewNavHeader>
            <Div flex={{
            align: 'center',
            gap: 2
          }}>
              <ViewTitle title="Get Started" />
              <Badge neutral subtle m={{
              l: 'auto'
            }}>
                {progress === steps.length - 1 ? 'Completed' : `${progress + 1} / ${steps.length - 1}`}
              </Badge>
            </Div>
            <StyledViewNavActions>
              <Button icon="docs" outline as="a" href={LINKS.outpost.docs} target="_blank" rel="noreferrer">
                View Docs
              </Button>
              <Button icon="chat" outline onClick={() => showChat()}>
                Get Help
              </Button>
            </StyledViewNavActions>
          </StyledViewNavHeader>
        </StyledViewNav>
        <Container xxlarge p={{
        t: 20,
        b: 20
      }}>
          <Container large>
            <Div m={{
            b: 14
          }}>
              <Text size="3xl" bold m={{
              b: 1
            }}>
                Getting started with Hookdeck's Outpost
              </Text>
              <Text muted max_w={{
              px: 728
            }}>
                Follow this guide to learn the key concepts and steps necessary to implement Outpost
                in your application and deliver your first event to a user.
              </Text>

              <Text semi muted flex={{
              align: 'center',
              gap: 2
            }} m={{
              t: 8
            }}>
                <svg height="32" viewBox="0 0 104 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="82" cy="22" r="21.5" fill={theme.colors.surface.base.variant_surface_3} stroke={theme.colors.outline.neutral} />
                  <path d="M77.8431 9.50215C78.8968 9.06861 80.044 8.91197 81.1754 9.04715C82.6337 9.21486 83.9331 9.83465 85.0735 10.9065C85.0881 10.9211 85.1085 10.9313 85.1274 10.9372C85.148 10.9422 85.1696 10.9422 85.1901 10.9372C86.6728 10.5543 88.2422 10.696 89.6322 11.3382L89.7008 11.3703L89.8699 11.4534C91.325 12.1909 92.46 13.4353 93.0608 14.9519C93.3656 15.6957 93.5172 16.4701 93.5201 17.278C93.5421 17.8788 93.4762 18.4797 93.3247 19.0615C93.3173 19.0909 93.3174 19.1217 93.325 19.151C93.3327 19.1803 93.3476 19.2073 93.3685 19.2292C94.2347 20.1144 94.8093 21.1688 95.0937 22.3938C95.5151 24.4719 95.0835 26.3459 93.8001 28.0142L93.6018 28.2563C92.7519 29.2293 91.6363 29.9328 90.392 30.2805C90.365 30.2884 90.3403 30.3026 90.3197 30.3218C90.2992 30.3411 90.2835 30.3649 90.2739 30.3913C89.9954 31.1949 89.7154 31.8832 89.1947 32.5701C87.8822 34.3011 85.9543 35.2622 83.7829 35.2505C82.0518 35.2417 80.5176 34.6088 79.1789 33.3517C79.1587 33.3329 79.134 33.3197 79.1071 33.3136C79.0802 33.3074 79.0521 33.3085 79.0258 33.3167C78.4599 33.499 77.8883 33.5253 77.2699 33.518C76.2851 33.5101 75.315 33.2777 74.4335 32.8384C73.5101 32.3808 72.7062 31.7142 72.0856 30.8915C71.8639 30.5969 71.6437 30.3199 71.4818 29.9917C71.2608 29.5415 71.0803 29.0725 70.9422 28.5903C70.6509 27.4931 70.6438 26.3397 70.9218 25.239C70.931 25.2128 70.934 25.1849 70.9306 25.1574C70.9256 25.1303 70.9117 25.1057 70.8912 25.0874C70.2175 24.4055 69.7027 23.5834 69.3833 22.6797C69.1709 22.123 69.0473 21.5364 69.0172 20.9413C68.9643 20.1576 69.0336 19.3704 69.2229 18.608C69.7143 16.9863 70.6549 15.7132 72.0418 14.7901C72.351 14.5844 72.6441 14.424 72.9183 14.3088C73.2318 14.179 73.5454 14.0697 73.8604 13.9778C73.8829 13.9708 73.9033 13.9584 73.9198 13.9416C73.9364 13.9248 73.9485 13.9041 73.9551 13.8815C74.1943 13.0226 74.6057 12.2213 75.1641 11.5263C75.8681 10.6324 76.7908 9.93518 77.8431 9.50215ZM82.921 24.9094C82.684 24.9227 82.4611 25.0263 82.298 25.1987C82.1349 25.3712 82.0441 25.5996 82.0441 25.8369C82.0441 26.0743 82.1349 26.3027 82.298 26.4752C82.4611 26.6476 82.684 26.7511 82.921 26.7644H88.2235C88.3497 26.7715 88.476 26.7528 88.5946 26.7094C88.7133 26.666 88.8219 26.5988 88.9137 26.512C89.0056 26.4251 89.0787 26.3205 89.1287 26.2044C89.1787 26.0884 89.2045 25.9633 89.2045 25.8369C89.2045 25.7106 89.1787 25.5855 89.1287 25.4695C89.0787 25.3534 89.0056 25.2487 88.9137 25.1619C88.8219 25.0751 88.7133 25.0079 88.5946 24.9645C88.476 24.9211 88.3497 24.9024 88.2235 24.9094H82.921ZM76.9651 18.0859C76.8389 17.8804 76.6377 17.7319 76.4041 17.6721C76.1704 17.6122 75.9227 17.6456 75.7132 17.7651C75.5036 17.8846 75.3489 18.0809 75.2815 18.3125C75.2141 18.5441 75.2395 18.7929 75.3522 19.0061L77.2072 22.2494L75.361 25.3644C75.2988 25.4693 75.258 25.5854 75.2407 25.706C75.2234 25.8267 75.23 25.9495 75.2602 26.0676C75.2904 26.1857 75.3436 26.2967 75.4167 26.3942C75.4898 26.4918 75.5813 26.574 75.6862 26.6361C75.791 26.6983 75.9071 26.7391 76.0278 26.7564C76.1484 26.7737 76.2713 26.7671 76.3894 26.7369C76.5075 26.7067 76.6184 26.6535 76.716 26.5804C76.8135 26.5073 76.8957 26.4158 76.9579 26.3109L79.0783 22.7307C79.1619 22.5897 79.2066 22.429 79.2079 22.265C79.2092 22.1011 79.167 21.9397 79.0856 21.7974L76.9651 18.0859Z" fill="url(#paint0_linear_1_26)" />
                  <circle cx="52" cy="22" r="21.5" fill={theme.colors.surface.base.variant_surface_3} stroke={theme.colors.outline.neutral} />
                  <path d="M63.4819 13.8637L51.6158 7.16108C51.2348 6.9458 50.7646 6.9458 50.3836 7.16108L38.5181 13.8637C38.1978 14.0447 38 14.3793 38 14.7418V28.2577C38 28.6202 38.1978 28.9548 38.5181 29.1358L50.3842 35.8384C50.7652 36.0537 51.2354 36.0537 51.6164 35.8384L63.4825 29.1358C63.8028 28.9548 64.0006 28.6202 64.0006 28.2577V14.7418C64.0006 14.3793 63.8028 14.0447 63.4825 13.8637H63.4819ZM62.7366 15.2835L51.2816 34.695C51.2042 34.8258 50.9997 34.7724 50.9997 34.6208V21.9104C50.9997 21.6564 50.861 21.4215 50.636 21.294L39.3854 14.9391C39.2517 14.8633 39.3063 14.6633 39.4612 14.6633H62.3711C62.6965 14.6633 62.8998 15.0083 62.7371 15.2841H62.7366V15.2835Z" fill={theme.mode === 'light' ? '#26251E' : '#FFFFFF'} />
                  <circle cx="22" cy="22" r="21.5" fill={theme.colors.surface.base.variant_surface_3} stroke={theme.colors.outline.neutral} />
                  <path d="M12.69 26.279L18.3934 23.0805L18.49 22.8026L18.3934 22.6479H18.1167L17.1621 22.5899L13.902 22.5017L11.0757 22.3845L8.33763 22.2371L7.64767 22.0909L7 21.239L7.06646 20.8137L7.64646 20.4258L8.47537 20.4983L10.312 20.6228L13.0646 20.8137L15.0608 20.9309L18.02 21.239H18.49L18.5565 21.0493L18.3946 20.9309L18.2701 20.8137L15.4209 18.8852L12.3372 16.8455L10.7229 15.671L9.84804 15.0777L9.40821 14.5195L9.21729 13.3015L10.01 12.429L11.0745 12.5015L11.3464 12.5753L12.4254 13.4042L14.7309 15.1877L17.7409 17.4025L18.1819 17.7699L18.3571 17.6454L18.3801 17.5572L18.1819 17.2261L16.5446 14.2705L14.7974 11.2618L14.0192 10.0148L13.8138 9.26683C13.7355 8.97956 13.6933 8.68366 13.6881 8.38596L14.592 7.16192L15.091 7L16.2945 7.16192L16.802 7.60175L17.5512 9.31033L18.7619 12.0037L20.6409 15.665L21.1919 16.75L21.4855 17.7554L21.5955 18.0635H21.7864V17.8871L21.941 15.8257L22.2274 13.2942L22.5053 10.0378L22.602 9.11942L23.0563 8.01983L23.959 7.42533L24.6646 7.76367L25.2446 8.59138L25.1637 9.12788L24.8181 11.3645L24.1426 14.8723L23.7028 17.2189H23.959L24.2526 16.9265L25.4428 15.3484L27.439 12.8544L28.321 11.8635L29.3481 10.7712L30.0091 10.2504H31.2573L32.1756 11.6146L31.7648 13.0235L30.4791 14.6512L29.4146 16.0311L27.8872 18.0852L26.9327 19.7286L27.0209 19.8615L27.248 19.8373L30.699 19.1051L32.5635 18.7668L34.788 18.3861L35.7946 18.855L35.9045 19.3323L35.5082 20.3074L33.129 20.8946L30.339 21.4529L26.1835 22.4353L26.1327 22.4715L26.192 22.5452L28.0637 22.7216L28.8636 22.7651H30.8235L34.4727 23.037L35.4272 23.6677L36 24.4387L35.9045 25.0247L34.4364 25.7739L32.4548 25.3038L27.828 24.2043L26.2427 23.8067H26.0228V23.9396L27.3435 25.2301L29.7674 27.4172L32.7991 30.2326L32.9526 30.931L32.5635 31.4808L32.1527 31.4216L29.4883 29.4194L28.46 28.5168L26.1327 26.5593H25.9781V26.7647L26.5146 27.5489L29.3481 31.8035L29.4955 33.1085L29.2901 33.535L28.5555 33.7924L27.7483 33.645L26.088 31.3189L24.3783 28.7005L22.9971 26.3527L22.828 26.4493L22.0135 35.2146L21.6317 35.6617L20.7508 36L20.0174 35.443L19.6283 34.5403L20.0174 32.7568L20.4874 30.432L20.868 28.5833L21.2136 26.2874L21.419 25.5238L21.4045 25.473L21.2354 25.4948L19.5026 27.8715L16.8685 31.4301L14.7829 33.6595L14.2826 33.8576L13.4163 33.4105L13.4972 32.6106L13.9818 31.8989L16.8673 28.2304L18.6073 25.9563L19.731 24.6441L19.7238 24.4532H19.6573L11.9928 29.4267L10.6274 29.6031L10.039 29.0521L10.1127 28.1507L10.3918 27.857L12.6973 26.2717L12.69 26.279Z" fill="#D97757" />
                  <defs>
                    <linearGradient id="paint0_linear_1_26" x1="82.1247" y1="9.00049" x2="82.1247" y2="35.2505" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#B1A7FF" />
                      <stop offset="0.5" stopColor="#7A9DFF" />
                      <stop offset="1" stopColor="#3941FF" />
                    </linearGradient>
                    <clipPath id="clip0_1_26">
                      <rect width="104" height="44" fill="white" />
                    </clipPath>
                    <clipPath id="clip1_1_26">
                      <rect width="26" height="29" fill="white" transform="translate(38 7)" />
                    </clipPath>
                    <clipPath id="clip2_1_26">
                      <rect width="29" height="29" fill="white" transform="translate(7 7)" />
                    </clipPath>
                  </defs>
                </svg>
                Ask your agent to help —
                <Link neutral onClick={handleCopyPromptClick} target="_blank" m={{
                l: -1
              }}>
                  Copy prompt
                </Link>
              </Text>
            </Div>

            {steps.map((step_0, index) => index <= visible_step_index ? <Div key={step_0.key} flex style={progress < index ? {
            opacity: 0.2,
            pointerEvents: 'none',
            position: 'relative'
          } : {
            position: 'relative'
          }}>
                  <span id={step_0.key} style={{
              position: 'absolute',
              top: '-120px'
            }} />
                  <StyledProgress is_last={steps.length - 1 === index} required={!step_0.optional} is_last_primary={true}>
                    <Icon p={0.5} icon={step_0.icon as IconName} />
                  </StyledProgress>
                  <Div p={{
              l: 12,
              b: 20
            }} flex={{
              grow: true,
              direction: 'column'
            }} max_w={100}>
                    <Div>
                      <Div flex={{
                  align: 'center',
                  gap: 2
                }} m={{
                  b: 1
                }}>
                        <Text bold size="l">
                          {step_0.label}
                        </Text>
                        {step_0.optional && <Badge neutral m={{
                    l: 1
                  }}>
                            Optional
                          </Badge>}
                      </Div>
                      <Text as="p" muted m={{
                  b: 4
                }}>
                        {typeof step_0.description === 'function' ? step_0.description(context) : step_0.description}
                      </Text>
                    </Div>
                    {step_0.Component && <step_0.Component context={context} is_current_step={progress === index} nextStep={() => setManualProgress(index + 1)} mutateContext={mutateContext} />}
                  </Div>
                </Div> : null)}
          </Container>
        </Container>
      </StyledViewContent>
    </StyledViewLayout>;
};
export default GetStarted;